import React, { useEffect, useRef, useState } from 'react';
import Appearance from 'styles/Appearance.js';
import PageControl from 'views/PageControl.js';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import Views from 'views/Main.js';

const UserLookup = ({ appendContent, containerStyle, metadata = [], onChange, placeholder, removable = false, utils, value }) => {

    const limit = 10;
    const offset = useRef(0);
    const textTimeout = useRef(null);

    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [results, setResults] = useState([]);
    const [selection, setSelection] = useState(null);
    const [text, setText] = useState('');

    const onClearSelection = () => {

        // clear text state and selection state
        setText('');
        setSelection(null);

        // notify subscriber of data change
        if(typeof(onChange) === 'function') {
            onChange(null);
        }
    }

    const onClick = async user => {
        try {

            let target = { ...user };

            // clear results and set selected user
            setResults([]);
            setText(user.full_name);
            setSelection(user);

            // determine if metadata was requested
            if(metadata.length > 0) {

                // set loading flag and submit request to server
                setLoading(true);
                const response = await Request.get(utils, '/utils/', {
                    type: 'user_lookup_result_metadata',
                    user_id: user.user_id,
                    ...metadata.reduce((object, key) => ({
                        ...object,
                        [key]: true
                    }), {})
                });

                // update target with responses values
                setLoading(false);
                target = { ...target, ...response };
            }

            // notify subscribers of selection change
            if(typeof(onChange) === 'function') {
                onChange(target);
            }
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                message: `There was an issue retrieving the details for this user. ${e.message || 'An unknown error occurred'}`,
                title: 'Oops!'
            });
        }
    }

    const onFieldChange = async input => {
        
        // clear existing timeout if applicable
        if(textTimeout.current) {
            clearTimeout(textTimeout.current);
        }

        // set new text timeout
        textTimeout.current = setTimeout(() => {
            setText(text => {
                if(text !== input) {
                    fetchResults(input);
                }
                return input;
            });
        }, 500);
    }

    const getResults = () => {
        if(results.length > 0) {
            return (
                <div style={{
                    backgroundColor: Appearance.colors.textField(),
                    borderColor: Appearance.colors.divider(),
                    borderRadius: 12,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    marginTop: 8,
                    overflow: 'hidden',
                    padding: 0,
                    textAlign: 'left'
                }}>
                    {results.map((user, index) => {
                        return (
                            Views.entry({
                                bottomBorder: index !== results.length - 1,
                                hideIcon: true,
                                key: index,
                                onClick: onClick.bind(this, user),
                                style: { padding: '6px 12px 6px 12px' },
                                subTitle: user.email_address,
                                title: user.full_name
                            })
                        )
                    })}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            fetchResults(text);
                        }} />
                    )}
                </div>
            )
        }
    }

    const fetchResults = async text => {
        try {

            // no additional logic is required if no text was provided
            if(!text || text.length === 0) {
                setResults([]);
                return;
            }

            // send request to server
            setLoading(true);
            let { paging, users } = await Request.get(utils, '/utils/', {
                limit: limit,
                offset: offset.current,
                search_text: text,
                type: 'user_lookup'
            });
            
            // end loading and update local state with results
            setLoading(false);
            setPaging(paging);
            setResults(users);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                message: `There was an issue completing your search. ${e.message || 'An unknown error occurred'}`,
                title: 'Oops!'
            });
        }
    }

    useEffect(() => {
        setText(value?.full_name);
        if(value) {
            setSelection(value);
        }
    }, [value]);

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            ...containerStyle
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                minWidth: 0,
                width: '100%'
            }}>
                <TextField
                appendContent={appendContent}
                autoCapitalize={'sentences'}
                autoComplete={false}
                autoCorrect={false}
                loading={loading}
                onChange={onFieldChange}
                placeholder={placeholder || 'Search by first and last name...'}
                value={text}
                style={{
                    flexGrow: 1,
                    ...Appearance.textStyles.title()
                }} />
                {selection?.user_id > 0 && removable === true && (
                    <img
                    className={'text-button'}
                    onClick={onClearSelection}
                    src={'images/delete-red-small.png'}
                    style={{
                        height: 25,
                        marginLeft: 8,
                        width: 25
                    }} />
                )}
            </div>
            {getResults()}
        </div>
    )
}
export default UserLookup;
